import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, AlertCircle, Clock } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { validateEventPassword } from "@/services/api/publicEvents";
import { toast } from "sonner";
import { getPasswordInvalidMessage, getPasswordButtonText } from "@/i18n/helpers";

interface PasswordGateProps {
  eventSlug: string;
  onPasswordValidated: (accessToken?: string) => void;
}

const MAX_ATTEMPTS = 3; // Should match backend config
const INITIAL_BACKOFF_SECONDS = 60; // 1 minute
const BACKOFF_MULTIPLIER = 2; // Exponential backoff

const lockoutStorageKey = (eventSlug: string) => `pwd-gate-lockout:${eventSlug}`;

const readStoredLockout = (eventSlug: string): Date | null => {
  try {
    const raw = window.localStorage.getItem(lockoutStorageKey(eventSlug));
    if (!raw) return null;
    const until = new Date(raw);
    if (Number.isNaN(until.getTime()) || until <= new Date()) {
      window.localStorage.removeItem(lockoutStorageKey(eventSlug));
      return null;
    }
    return until;
  } catch {
    return null;
  }
};

export function PasswordGate({ eventSlug, onPasswordValidated }: PasswordGateProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(() => readStoredLockout(eventSlug) !== null);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(() => readStoredLockout(eventSlug));
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  // Persist lockout so a reload (or fresh visit) during lockout still shows the gate locked
  useEffect(() => {
    try {
      if (lockoutUntil) {
        window.localStorage.setItem(lockoutStorageKey(eventSlug), lockoutUntil.toISOString());
      } else {
        window.localStorage.removeItem(lockoutStorageKey(eventSlug));
      }
    } catch {
      // localStorage unavailable (private mode) — lockout stays in-memory only
    }
  }, [lockoutUntil, eventSlug]);

  // Check if we're still locked out
  useEffect(() => {
    if (lockoutUntil) {
      const checkLockout = () => {
        const now = new Date();
        if (now >= lockoutUntil) {
          setIsLocked(false);
          setLockoutUntil(null);
          setAttempts(0);
          setRetryAfter(null);
          setError(null);
        } else {
          const secondsRemaining = Math.ceil((lockoutUntil.getTime() - now.getTime()) / 1000);
          setRetryAfter(secondsRemaining);
          // Arriving already locked (restored from storage): show the lockout message
          setError((prev) => prev ?? t("events.password.maxAttemptsReached", {
            minutes: Math.ceil(secondsRemaining / 60),
          }));
        }
      };

      checkLockout();
      const interval = setInterval(checkLockout, 1000);
      return () => clearInterval(interval);
    }
  }, [lockoutUntil, eventSlug, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError(t("events.password.required"));
      return;
    }

    if (isLocked) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await validateEventPassword(eventSlug, password);
      
      if (result.success) {
        // Password validated successfully
        setAttempts(0);
        setIsLocked(false);
        setLockoutUntil(null);
        setRemainingAttempts(null);
        onPasswordValidated(result.accessToken);
        toast.success(t("events.password.validated"));
      } else {
        // Password validation failed
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setRemainingAttempts(result.remainingAttempts ?? null);

        if (result.retryAfter) {
          // Rate limited - exponential backoff
          const lockoutTime = new Date(Date.now() + result.retryAfter * 1000);
          setLockoutUntil(lockoutTime);
          setIsLocked(true);
          setRetryAfter(result.retryAfter);
          setError(
            result.message || 
            t("events.password.rateLimited", { 
              minutes: Math.ceil(result.retryAfter / 60)
            })
          );
        } else if (newAttempts >= MAX_ATTEMPTS) {
          // Hit max attempts - apply exponential backoff
          const backoffSeconds = INITIAL_BACKOFF_SECONDS * Math.pow(BACKOFF_MULTIPLIER, newAttempts - MAX_ATTEMPTS);
          const lockoutTime = new Date(Date.now() + backoffSeconds * 1000);
          setLockoutUntil(lockoutTime);
          setIsLocked(true);
          setRetryAfter(backoffSeconds);
          setError(
            t("events.password.maxAttemptsReached", {
              minutes: Math.ceil(backoffSeconds / 60),
            })
          );
        } else {
          // Still have attempts remaining
          setError(
            result.message || 
            getPasswordInvalidMessage(t, {
              remainingAttempts: result.remainingAttempts,
            })
          );
        }
      }
    } catch (err: unknown) {
      console.error("Password validation error:", err);
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        t("events.password.error")
      );
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900/20">
            <Lock className="h-6 w-6 text-pink-600 dark:text-pink-400" />
          </div>
          <CardTitle className="text-2xl">
            {t("events.password.title")}
          </CardTitle>
          <CardDescription>
            {t("events.password.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant={isLocked ? "destructive" : "default"}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isLocked && retryAfter !== null && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  {t("events.password.retryAfter", {
                    time: formatTimeRemaining(retryAfter),
                  })}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">
                {t("events.password.label")}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("events.password.placeholder")}
                disabled={loading || isLocked}
                autoFocus
              />
            </div>

            {remainingAttempts !== null && remainingAttempts > 0 && !isLocked && (
              <p className="text-sm text-muted-foreground text-center">
                {t("events.password.remainingAttempts", {
                  count: remainingAttempts,
                })}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || isLocked || !password.trim()}
            >
              {getPasswordButtonText(t, { loading, locked: isLocked })}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}


